import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { PaymentType } from "../enums/payment-type";
import { TelnyxService } from "./telnyx.service";
import { map, startWith, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { IPaginatedResponse } from "../models/api/paginated-response";
import { IPaginationSearchParams } from "../models/api/pagination-search-params";
import { IAddClientInfoPayload } from "../models/client/add-client-info-payload";
import { IChangePrimaryClientNumberSearchParams } from "../models/client/change-primary-client-number-search-params";
import { IClientFlags } from "../models/client/client-flags";
import { IClientInfo } from "../models/client/client-info";
import { IClientNumber } from "../models/client/client-number";
import { IClientStatistics } from "../models/client/client-statistics";
import { ICreatePaymentLinkSearchParams } from "../models/client/create-payment-link-search-params";
import { ICurrentSubscription } from "../models/client/current-subscription";
import { IPaymentLink } from "../models/client/payment-link";
import { ISubscriptionPlan } from "../models/client/subscription-plan";
import { IUpdateClientInfoPayload } from "../models/client/update-client-info-payload";

@Injectable({ providedIn: "root" })
export class ClientService {
  private readonly _numbersChanged = new Subject<void>();

  private _lastKnownClientFullName: string | null = null;

  constructor(private readonly _httpClient: HttpClient, private readonly _telnyxService: TelnyxService) {}

  get lastKnownClientFullName(): string | null {
    return this._lastKnownClientFullName;
  }

  getClientFlags() {
    return this._httpClient.get<IClientFlags>(this.withBaseUrl("/flags"));
  }

  getClientInfo() {
    return this._httpClient.get<IClientInfo>(this.withBaseUrl("/info"));
  }

  getClientFullName() {
    return this.getClientInfo().pipe(
      map(clientInfo => {
        const firstName = clientInfo?.profileInfo?.firstName ?? "";
        const lastName = clientInfo?.profileInfo?.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();

        this._lastKnownClientFullName = fullName || clientInfo?.login || "User";

        return this._lastKnownClientFullName;
      }),
    );
  }

  addClientInfo(payload: IAddClientInfoPayload) {
    return this._httpClient.post<void>(this.withBaseUrl("/info"), payload);
  }

  updateClientInfo(payload: IUpdateClientInfoPayload) {
    return this._httpClient.put<void>(this.withBaseUrl("/info"), payload);
  }

  getStatistics() {
    return this._httpClient.get<IClientStatistics>(this.withBaseUrl("/statistic"));
  }

  getNumbers() {
    return this._httpClient.get<IClientNumber[]>(this.withBaseUrl("/numbers"));
  }

  watchNumbers() {
    return this._numbersChanged.pipe(
      startWith(undefined),
      switchMap(() => this.getNumbers())
    );
  }

  changePrimaryNumber(params: IChangePrimaryClientNumberSearchParams) {
    return this._httpClient.put<IClientNumber>(this.withBaseUrl("/primary-number"), {}, { params });
  }

  createPaymentLink(params: ICreatePaymentLinkSearchParams) {
    return this._httpClient.post<IPaymentLink>(this.withBaseUrl("/payment"), {}, { params });
  }

  createExtraPagesPaymentLink() {
    return this._httpClient.post<IPaymentLink>(this.withBaseUrl("/extra-pages"), null);
  }

  purchaseFaxNumber(params: ICreatePaymentLinkSearchParams): Observable<IPaymentLink | null> {
    if (params.type !== PaymentType.FaxNumber) {
      return this.createPaymentLink(params);
    }

    return this.getClientFlags().pipe(
      switchMap(flags => flags.hasFaxPhoneNumber
        ? this.createPaymentLink(params)
        : this._telnyxService.addPrimaryNumber({ number: params.number }).pipe(
          tap(() => this._numbersChanged.next()),
          map(() => null)
        ))
    );
  }

  getSubscriptions(params: IPaginationSearchParams) {
    return this._httpClient.get<IPaginatedResponse<ISubscriptionPlan>>(this.withBaseUrl("/subscriptions"), { params });
  }

  getCurrentSubscription() {
    return this._httpClient.get<ICurrentSubscription>(this.withBaseUrl("/subscription"));
  }

  private withBaseUrl(endpoint: string) {
    return `${environment.serverUrl}/client${endpoint}`;
  }
}
